import React, { useEffect, useMemo, useRef, useState } from "react";
import { Global } from "../helpers/Global";

export const FinalizarInterManualGerente = () => {
  const [ticajes, setTicajes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [mensaje, setMensaje] = useState("");
  const [formData, setFormData] = useState({});
  const [working, setWorking] = useState({});
  const [cargando, setCargando] = useState(true);

  const canAbort = typeof AbortController !== "undefined";
  const abortRef = useRef(null);

  useEffect(() => {
    fetchTodo();
    return () => { if (canAbort && abortRef.current) abortRef.current.abort(); };
    // eslint-disable-next-line
  }, []);

  const fetchJSON = async (path, signal) => {
    const url = `${Global.url}${path}${path.includes("?") ? "&" : "?"}t=${Date.now()}`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      cache: "no-store",
      signal,
    });
    if (res.status === 204) return null;
    if (!res.ok) throw new Error(`HTTP_${res.status}`);
    try { return await res.json(); } catch { return null; }
  };

  const fetchTodo = async () => {
    setCargando(true);
    setMensaje("");
    try {
      if (canAbort && abortRef.current) abortRef.current.abort();
      const ac = canAbort ? new AbortController() : null;
      abortRef.current = ac;

      // 1) Intervenciones en proceso (OBLIGATORIO)
      const dataT = await fetchJSON("ticajes/proceso", ac?.signal);
      setTicajes(Array.isArray(dataT) ? dataT : dataT ? [dataT] : []);

      // 2) Usuarios (OPCIONAL, para mostrar nombres)
      try {
        const dataU = await fetchJSON("usuarios", ac?.signal);
        setUsuarios(Array.isArray(dataU) ? dataU : []);
      } catch {
        setUsuarios([]); // no pasa nada, usaremos los campos del ticaje
      }
    } catch (err) {
      console.error("Error cargando datos:", err);
      setMensaje("No se pudieron obtener las intervenciones en proceso.");
      setTimeout(() => setMensaje(""), 4000);
      setTicajes([]);
    } finally {
      setCargando(false);
    }
  };

  const toIsoFromLocalDatetime = (value) => {
    if (!value) return value;
    const [date, time] = value.split("T");
    const [y, m, d] = date.split("-").map(Number);
    const [hh, mm] = time.split(":").map(Number);
    const dt = new Date(y, m - 1, d, hh, mm, 0);
    return dt.toISOString();
  };

  const handleChange = (ticajeId, campo, valor) => {
    setFormData((prev) => ({
      ...prev,
      [ticajeId]: { ...prev[ticajeId], [campo]: valor },
    }));
  };

  const mark = (id, v) => setWorking((w) => ({ ...w, [id]: v }));

  const finalizarTicaje = async (ticajeId) => {
    const datos = formData[ticajeId];
    if (!datos?.fecha_fin || !datos?.comentario) {
      setMensaje("Debes rellenar la fecha de fin y el motivo del cierre.");
      setTimeout(() => setMensaje(""), 3000);
      return;
    }
    if (working[ticajeId]) return;

    const finISO = toIsoFromLocalDatetime(datos.fecha_fin);
    mark(ticajeId, true);
    try {
      const res = await fetch(`${Global.url}ticajes/finalizar-manualmente?t=${Date.now()}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        cache: "no-store",
        body: JSON.stringify({
          id: ticajeId,
          fin: finISO,
          observaciones_admin: datos.comentario,
        }),
      });

      let result = null;
      try { result = await res.json(); } catch {}

      if (res.ok) {
        setMensaje("Intervención finalizada correctamente.");
        setFormData((prev) => ({ ...prev, [ticajeId]: {} }));
        await fetchTodo();
      } else {
        setMensaje((result && result.message) || `Error al finalizar (HTTP ${res.status})`);
      }
    } catch (error) {
      console.error("❌ Error al finalizar (Gerente):", error);
      setMensaje("Error de conexión.");
    } finally {
      setTimeout(() => setMensaje(""), 4000);
      mark(ticajeId, false);
    }
  };

  // Mapa de usuarios para nombre completo (opcional)
  const mapUsuario = useMemo(() => {
    const m = {};
    for (const u of usuarios) {
      const full = [u.nombre, u.apellidos].filter(Boolean).join(" ");
      m[String(u.id)] = full || u.nombre || `Usuario ${u.id}`;
    }
    return m;
  }, [usuarios]);

  const getNombreUsuario = (t) => {
    const uid = String(t.usuario_id ?? t.user_id ?? t.usuario?.id ?? "");
    return mapUsuario[uid] || t.usuario || uid || "-";
  };

  const getInicio = (t) => t.inicio ?? t.fecha_inicio ?? t.created_at ?? null;

  const ticajesOrdenados = useMemo(() => {
    return [...ticajes].sort((a, b) => new Date(getInicio(b) || 0) - new Date(getInicio(a) || 0));
  }, [ticajes]);

  return (
    <div className="consulta-ticajes">
      <h2 className="centrar-titulo">Finalizar Intervenciones en Proceso (Gerente)</h2>

      {mensaje && <div className="alerta">{mensaje}</div>}
      {cargando && <p>Cargando intervenciones...</p>}

      {(!ticajesOrdenados || ticajesOrdenados.length === 0) && !cargando ? (
        <p>No hay intervenciones activas actualmente.</p>
      ) : (
        <div className="table-listado-padre">
          <table className="table-listado">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Cliente</th>
                <th>Centro</th>
                <th>Orden</th>
                <th>Inicio</th>
                <th>Comentario</th>
                <th>Fecha Fin</th>
                <th>Motivo</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {ticajesOrdenados.map((t) => {
                const fila = formData[t.id] || {};
                const busy = !!working[t.id];
                const inicio = getInicio(t);

                return (
                  <tr key={t.id}>
                    <td>{getNombreUsuario(t)}</td>
                    <td>{t.cliente || t.cliente_nombre || t.cliente_id || "-"}</td>
                    <td>{t.centro || t.centro_nombre || t.centro_id || "-"}</td>
                    <td>{t.orden || (t.orden_id ? `OT ${t.orden_id}` : "-")}</td>
                    <td>{inicio ? new Date(inicio).toLocaleString() : "-"}</td>
                    <td>{t.comentarios || "Sin comentarios"}</td>
                    <td>
                      <input
                        type="datetime-local"
                        value={fila.fecha_fin || ""}
                        onChange={(e) => handleChange(t.id, "fecha_fin", e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        placeholder="Motivo del cierre"
                        value={fila.comentario || ""}
                        onChange={(e) => handleChange(t.id, "comentario", e.target.value)}
                      />
                    </td>
                    <td className="acciones-td">
                      <button
                        type="button"
                        className="btn-login"
                        onClick={() => finalizarTicaje(t.id)}
                        disabled={!fila.fecha_fin || !fila.comentario || busy}
                        aria-busy={busy}
                      >
                        {busy ? "Procesando..." : "Finalizar"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
